import { useState, useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, useLocation, Navigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";

// Pages
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import CalendarPage from "@/pages/Calendar";
import Leads from "@/pages/Leads";
import Users from "@/pages/Users";
import Uploads from "@/pages/Uploads";
import Subscriptions from "@/pages/Subscriptions";
import SalesDashboard from "@/pages/SalesDashboard";
import InviteCodes from "@/pages/InviteCodes";
import EmailTools from "@/pages/EmailTools";
import SentItems from "@/pages/SentItems";
import EmailTemplates from "@/pages/EmailTemplates";
import Sequences from "@/pages/Sequences";
import Suppressions from "@/pages/Suppressions";
import Settings from "@/pages/Settings";
import TeamActivity from "@/pages/TeamActivity";
import ApiKeys from "@/pages/ApiKeys";
import OAuthConsent from "@/pages/OAuthConsent";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
export const useAuth = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem("token"));

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setUser(response.data);
      return response.data;
    } catch (error) {
      setUser(null);
      localStorage.removeItem("token");
      setToken(null);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem("token", response.data.token);
    setToken(response.data.token);
    setUser(response.data.user);
    return response.data.user;
  };

  const register = async (email, password, name, role) => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name, role });
    localStorage.setItem("token", response.data.token);
    setToken(response.data.token);
    setUser(response.data.user);
    return response.data.user;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (e) {
      console.error("Logout error:", e);
    }
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
  };

  useEffect(() => {
    checkAuth();
  }, []);

  return { user, setUser, loading, login, register, logout, token, checkAuth };
};

// Protected Route Component
const ProtectedRoute = ({ children, requireManager = false, requireSuperAdmin = false }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(location.state?.user ? true : null);
  const [user, setUser] = useState(location.state?.user || null);
  const [token] = useState(localStorage.getItem("token"));

  useEffect(() => {
    if (location.state?.user) {
      setUser(location.state.user);
      setIsAuthenticated(true);
      return;
    }

    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`, {
          withCredentials: true,
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        setUser(response.data);
        setIsAuthenticated(true);
      } catch (error) {
        setIsAuthenticated(false);
        navigate('/login');
      }
    };
    checkAuth();
  }, [location.state, navigate, token]);

  // Heartbeat for the Team Activity online-presence detection. Pings
  // /api/heartbeat on mount + every minute while the user is on any
  // authenticated page. Stops on unmount or sign-out.
  useEffect(() => {
    if (!isAuthenticated) return;
    const ping = () => {
      const t = localStorage.getItem("token");
      if (!t) return;
      axios.post(`${API}/heartbeat`, {}, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${t}` },
      }).catch(() => { /* silent — heartbeat is best-effort */ });
    };
    ping();
    const id = setInterval(ping, 60_000);
    return () => clearInterval(id);
  }, [isAuthenticated]);

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  if (requireSuperAdmin && !user?.is_super_admin) {
    return <Navigate to="/dashboard" />;
  }

  if (requireManager && user?.role !== "manager") {
    return <Navigate to="/dashboard" />;
  }

  return children({ user, setUser });
};

function AppRouter() {
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute>
            {({ user, setUser }) => <Dashboard user={user} setUser={setUser} />}
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/calendar" 
        element={
          <ProtectedRoute>
            {({ user, setUser }) => <CalendarPage user={user} setUser={setUser} />}
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/leads" 
        element={
          <ProtectedRoute>
            {({ user, setUser }) => <Leads user={user} setUser={setUser} />}
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/uploads" 
        element={
          <ProtectedRoute requireManager>
            {({ user, setUser }) => <Uploads user={user} setUser={setUser} />}
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/users" 
        element={
          <ProtectedRoute requireManager>
            {({ user, setUser }) => <Users user={user} setUser={setUser} />}
          </ProtectedRoute>
        } 
      />
      <Route
        path="/subscriptions"
        element={
          <ProtectedRoute requireManager>
            {({ user, setUser }) => <Subscriptions user={user} setUser={setUser} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/sales-dashboard"
        element={
          <ProtectedRoute requireManager>
            {({ user, setUser }) => <SalesDashboard user={user} setUser={setUser} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/invite-codes"
        element={
          <ProtectedRoute>
            {({ user, setUser }) => <InviteCodes user={user} setUser={setUser} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/email-tools"
        element={
          <ProtectedRoute requireSuperAdmin>
            {({ user, setUser }) => <EmailTools user={user} setUser={setUser} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/sent-items"
        element={
          <ProtectedRoute requireSuperAdmin>
            {({ user, setUser }) => <SentItems user={user} setUser={setUser} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/email-templates"
        element={
          <ProtectedRoute requireSuperAdmin>
            {({ user, setUser }) => <EmailTemplates user={user} setUser={setUser} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/sequences"
        element={
          <ProtectedRoute requireSuperAdmin>
            {({ user, setUser }) => <Sequences user={user} setUser={setUser} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/suppressions"
        element={
          <ProtectedRoute requireSuperAdmin>
            {({ user, setUser }) => <Suppressions user={user} setUser={setUser} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute requireSuperAdmin>
            {({ user, setUser }) => <Settings user={user} setUser={setUser} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/team-activity"
        element={
          <ProtectedRoute requireManager>
            {({ user, setUser }) => <TeamActivity user={user} setUser={setUser} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/api-keys"
        element={
          <ProtectedRoute requireSuperAdmin>
            {({ user, setUser }) => <ApiKeys user={user} setUser={setUser} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/oauth-consent"
        element={
          <ProtectedRoute>
            {({ user, setUser }) => <OAuthConsent user={user} setUser={setUser} />}
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <Toaster position="bottom-right" richColors />
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
    </div>
  );
}

export default App;
